'use client'
import * as React from 'react'
import {
  Check,
  Copy,
  Eye,
  Flame,
  RotateCcw,
  ShoppingBag,
  Star,
  Users,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ColorPicker } from '@/components/ui/color-picker'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { appSignupUrl } from '@/lib/widget/embed'

// Pure-browser social-proof popup composer. The visitor picks a popup
// type, a corner, an accent colour and a tokenised message, sees a real
// animated popup cycle in a mocked browser frame, and copies an embed
// snippet. Honest split: the preview runs on sample data (real Idukki
// marketing product names); live popups pull the store's real orders /
// visitor counts in-app once connected.

type PopupType = 'recent-purchase' | 'live-visitors' | 'low-stock' | 'recent-review'
type Position = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'

const TYPES: { value: PopupType; label: string; icon: React.ReactNode; template: string }[] = [
  {
    value: 'recent-purchase',
    label: 'Recent purchase',
    icon: <ShoppingBag className="h-3.5 w-3.5" aria-hidden />,
    template: '{name} from {city} just bought {product}',
  },
  {
    value: 'live-visitors',
    label: 'Live visitors',
    icon: <Users className="h-3.5 w-3.5" aria-hidden />,
    template: '{count} people are viewing {product} right now',
  },
  {
    value: 'low-stock',
    label: 'Low stock',
    icon: <Flame className="h-3.5 w-3.5" aria-hidden />,
    template: 'Only {count} left of {product} — selling fast',
  },
  {
    value: 'recent-review',
    label: 'Recent review',
    icon: <Star className="h-3.5 w-3.5" aria-hidden />,
    template: '{name} rated {product} 5 stars',
  },
]

const POSITIONS: { value: Position; label: string }[] = [
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'bottom-right', label: 'Bottom right' },
  { value: 'top-left', label: 'Top left' },
  { value: 'top-right', label: 'Top right' },
]

// Sample data uses the canonical Idukki marketing product names.
const SAMPLE = [
  { name: 'Maya', city: 'Austin', product: 'Airlift Overcoat Brown', count: 7 },
  { name: 'Liam', city: 'Manchester', product: 'White Sweater Green Stripes', count: 4 },
  { name: 'Priya', city: 'Toronto', product: 'WROGN Men Silver-Toned Watch', count: 12 },
  { name: 'Noah', city: 'Berlin', product: 'Airlift High-Waist Suit Up Shorts', count: 3 },
  { name: 'Sofia', city: 'Lisbon', product: 'Women Oval Sunglasses', count: 9 },
]

const DEFAULT_ACCENT = '#0b1120'

function fillTemplate(tpl: string, row: (typeof SAMPLE)[number]): string {
  return tpl
    .replace(/\{name\}/g, row.name)
    .replace(/\{city\}/g, row.city)
    .replace(/\{product\}/g, row.product)
    .replace(/\{count\}/g, String(row.count))
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false)
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const handler = () => setReduced(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return reduced
}

const POPUP_KEYFRAMES = `
@keyframes idk-sp-in {
  from { opacity: 0; transform: translate3d(0, 12px, 0) scale(0.98); }
  to   { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .idk-sp-card { animation: none !important; }
}
`

export function SocialProofBuilder() {
  const [type, setType] = React.useState<PopupType>('recent-purchase')
  const [position, setPosition] = React.useState<Position>('bottom-left')
  const [accent, setAccent] = React.useState(DEFAULT_ACCENT)
  const [message, setMessage] = React.useState(TYPES[0].template)
  const [delay, setDelay] = React.useState(4) // seconds between popups
  const [copied, setCopied] = React.useState(false)

  const reduced = usePrefersReducedMotion()
  const [idx, setIdx] = React.useState(0)
  const [visible, setVisible] = React.useState(true)

  // When the type changes, swap to that type's default template.
  function pickType(t: PopupType) {
    setType(t)
    const def = TYPES.find((x) => x.value === t)
    if (def) setMessage(def.template)
  }

  // Cycle the sample rows. With reduced motion we still rotate content
  // but without the slide animation (handled by CSS gating).
  React.useEffect(() => {
    const period = Math.max(2, delay) * 1000
    let swapTimer: ReturnType<typeof setTimeout>
    const interval = setInterval(() => {
      setVisible(false)
      swapTimer = setTimeout(() => {
        setIdx((i) => (i + 1) % SAMPLE.length)
        setVisible(true)
      }, 280)
    }, period)
    return () => {
      clearInterval(interval)
      clearTimeout(swapTimer)
    }
  }, [delay])

  const row = SAMPLE[idx]
  const text = fillTemplate(message, row)

  const meta = TYPES.find((t) => t.value === type)!

  const snippet = buildSnippet({ type, position, accent, message, delay })

  function onCopy() {
    navigator.clipboard?.writeText(snippet).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      },
      () => {},
    )
  }

  function reset() {
    setType('recent-purchase')
    setPosition('bottom-left')
    setAccent(DEFAULT_ACCENT)
    setMessage(TYPES[0].template)
    setDelay(4)
  }

  const posClass: Record<Position, string> = {
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'top-left': 'top-12 left-4',
    'top-right': 'top-12 right-4',
  }

  return (
    <div className="rounded-2xl border bg-card shadow-sm">
      <style>{POPUP_KEYFRAMES}</style>
      <div className="flex items-center justify-between border-b p-5 sm:p-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Social proof</p>
          <h2 className="mt-1 font-display text-xl font-medium tracking-tight sm:text-2xl">
            Build a social-proof popup, see it live
          </h2>
        </div>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <RotateCcw className="h-3 w-3" aria-hidden /> Reset
        </button>
      </div>

      <div className="grid gap-6 p-5 sm:p-6 md:grid-cols-2 md:gap-8 md:p-8">
        {/* Controls */}
        <div className="space-y-5">
          <Field label="Popup type">
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => pickType(t.value)}
                  aria-pressed={type === t.value}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    type === t.value
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Position">
            <div className="grid grid-cols-2 gap-2">
              {POSITIONS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPosition(p.value)}
                  aria-pressed={position === p.value}
                  className={cn(
                    'rounded-md border px-3 py-2 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    position === p.value
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Accent colour" hint="The icon chip + product link tint">
            <ColorPicker value={accent} onChange={setAccent} ariaLabel="Popup accent colour" />
          </Field>

          <Field
            label="Message template"
            hint="Tokens: {name} {city} {product} {count} — filled from live order data in-app"
          >
            <Input
              id="sp-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={meta.template}
            />
          </Field>

          <Field label={`Delay between popups: ${delay}s`} hint="How long each notification waits before the next">
            <input
              id="sp-delay"
              type="range"
              min={2}
              max={10}
              step={1}
              value={delay}
              onChange={(e) => setDelay(Number(e.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-secondary accent-primary"
              aria-valuetext={`${delay} seconds`}
            />
          </Field>
        </div>

        {/* Live preview in a mocked browser frame */}
        <div>
          <div className="overflow-hidden rounded-xl border bg-background shadow-sm">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 border-b bg-secondary/50 px-3 py-2">
              <span className="flex gap-1.5" aria-hidden>
                <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
                <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
                <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
              </span>
              <span className="ml-2 truncate rounded bg-background px-2 py-0.5 text-[10px] text-muted-foreground">
                yourstore.com
              </span>
            </div>
            {/* Viewport */}
            <div className="relative h-[300px] bg-gradient-to-b from-secondary/20 to-background">
              {/* Skeleton page content */}
              <div className="pointer-events-none absolute inset-0 p-5" aria-hidden>
                <div className="h-3 w-1/3 rounded bg-muted-foreground/15" />
                <div className="mt-3 h-2 w-2/3 rounded bg-muted-foreground/10" />
                <div className="mt-2 h-2 w-1/2 rounded bg-muted-foreground/10" />
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <div className="aspect-square rounded-lg bg-muted-foreground/10" />
                  <div className="aspect-square rounded-lg bg-muted-foreground/10" />
                  <div className="aspect-square rounded-lg bg-muted-foreground/10" />
                </div>
              </div>

              {/* The live popup */}
              {visible && (
                <div
                  key={`${idx}-${position}`}
                  className={cn('idk-sp-card absolute z-10 w-[min(260px,80%)]', posClass[position])}
                  style={
                    reduced
                      ? undefined
                      : { animation: 'idk-sp-in 0.28s cubic-bezier(0.22, 1, 0.36, 1) both' }
                  }
                  role="status"
                  aria-live="off"
                >
                  <div className="flex items-start gap-3 rounded-xl border bg-card p-3 shadow-lg">
                    <span
                      className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white"
                      style={{ backgroundColor: accent }}
                      aria-hidden
                    >
                      {meta.icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs leading-snug text-foreground">{text}</p>
                      <p className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Check className="h-3 w-3 text-emerald-500" aria-hidden /> Verified by Idukki
                      </p>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 rounded p-0.5 text-muted-foreground/50 transition hover:text-muted-foreground"
                      aria-label="Dismiss preview popup"
                      onClick={() => setVisible(false)}
                    >
                      <X className="h-3 w-3" aria-hidden />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Eye className="h-3 w-3 text-primary" aria-hidden />
            Preview uses sample data. Live popups pull your real orders and visitor counts in-app.
          </p>
        </div>
      </div>

      {/* Embed snippet */}
      <div className="border-t p-5 sm:p-6 md:p-8">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Embed snippet</p>
          <Button type="button" variant="outline" size="sm" onClick={onCopy} className="shrink-0">
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" aria-hidden /> Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" aria-hidden /> Copy snippet
              </>
            )}
          </Button>
          <span aria-live="polite" className="sr-only">
            {copied ? 'Embed snippet copied to clipboard' : ''}
          </span>
        </div>
        <pre className="mt-3 overflow-x-auto rounded-xl border bg-secondary/30 p-4 text-xs leading-relaxed text-foreground/90">
          <code>{snippet}</code>
        </pre>
      </div>

      <div className="flex flex-col gap-3 border-t bg-secondary/30 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <p className="max-w-xl text-xs text-muted-foreground sm:text-sm">
          The snippet renders the popup with these settings. It stays inert until you connect a store in Idukki, then it
          fills the tokens with real, verified order and visitor data, never invented numbers.
        </p>
        <Button asChild size="sm">
          <a href={appSignupUrl({ ref: 'tool-social-proof' })}>Connect real order data</a>
        </Button>
      </div>
    </div>
  )
}

// Build a clean, copy-pasteable embed snippet for the Idukki social-proof
// runtime. Config travels as data-attributes so it works on any HTML site.
function buildSnippet(cfg: {
  type: PopupType
  position: Position
  accent: string
  message: string
  delay: number
}): string {
  const attrs = [
    'data-idukki="social-proof"',
    `data-type="${cfg.type}"`,
    `data-position="${cfg.position}"`,
    `data-accent="${cfg.accent}"`,
    `data-delay="${cfg.delay}"`,
    `data-message="${cfg.message.replace(/"/g, '&quot;')}"`,
  ].join('\n  ')
  return `<div\n  ${attrs}\n></div>\n<script src="https://app.idukki.io/dist/social-proof.js" defer></script>`
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      {hint && <span className="mt-0.5 block text-[11px] text-muted-foreground">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  )
}
